'use client'

import { ErrorLike } from '@apollo/client'
import { useQuery } from '@apollo/client/react'
import { useCallback, useEffect, useMemo } from 'react'
import { createContainer } from 'unstated-next'

import {
  OrganizationAssociationsQuery,
  OrganizationMinimalFieldsFragment,
  ProductCatalogCategoryKey,
  TeamStoreAllowedPlacementsFragment,
  TeamStoreCollectionIdentityFieldsFragment,
  TeamStoreDiscountPercentageFieldsFragment,
  TeamStoreEmbroideryConstraintFieldsFragment,
  TeamStoreMinimalFieldsFragment,
} from '../../__generated__/graphql/catalog/graphql'
import { ORG_ASSOCIATION_QUERY } from '../../lib/graphql/queries/organization/ORG_ASSOCIATION_QUERY'
import { ORG_IDENTITY_QUERY } from '../../lib/graphql/queries/organization/ORG_IDENTITY_QUERY'
import { QueryStatus } from '../../lib/hooks/types'

import { useAuthentication } from './AuthenticationProvider.client'
import { useCart } from './CartProvider.client'
import { useGraphqlClients } from './GraphqlClientsProvider.client'
import { useLocalization } from './LocalizationProvider.client'

interface UseOrganizationConfigResult {
  error?: ErrorLike | undefined
  portal?: OrganizationAssociationsQuery['my']['portal']
  organization?: OrganizationMinimalFieldsFragment
  organizations?: OrganizationMinimalFieldsFragment[]
  teamStore?: TeamStoreMinimalFieldsFragment &
    TeamStoreEmbroideryConstraintFieldsFragment &
    TeamStoreCollectionIdentityFieldsFragment
  teamStoreAllowedEmbroideryPlacements?: TeamStoreAllowedPlacementsFragment[]
  b2bStorefrontCollection: {
    name: string
    handle: string
  } | null
  stipend: number | null
  stipendCad: number | null
  discountPercentage: number
  status: QueryStatus
  redirectWhenNotAvailable: (
    options: Parameters<ReturnType<typeof useAuthentication>['navigateToLogin']>[0]
  ) => void
  hasMandatoryEmbroidery: (props: {
    categoryId: string | undefined | null
    categoryKey: ProductCatalogCategoryKey | undefined | null
  }) => boolean
  hasMandatoryTextEmbroidery: (props: {
    categoryId: string | undefined | null
    categoryKey: ProductCatalogCategoryKey | undefined | null
  }) => boolean
  hasMandatoryLogoEmbroidery: (props: {
    categoryId: string | undefined | null
    categoryKey: ProductCatalogCategoryKey | undefined | null
  }) => boolean
}

/* Fetch a users associated legacy portal and/or organizations. */
const useOrganizationAssociations = ({
  withPortalAssociation,
  withOrganizationAssociation,
  skip,
  onCompleted,
}: {
  withPortalAssociation: boolean
  withOrganizationAssociation: boolean
  skip: boolean
  onCompleted?: (data: OrganizationAssociationsQuery) => void
}) => {
  const { personalizedClient } = useGraphqlClients()
  return useQuery(ORG_ASSOCIATION_QUERY, {
    client: personalizedClient,
    skip: skip,
    variables: {
      withPortalAssociation,
      withOrganizationAssociation,
    },
    onCompleted,
  })
}

const useOrganizationConfigImpl = (): UseOrganizationConfigResult => {
  const { isLoggedIn, navigateToLogin } = useAuthentication()
  const { personalizedClient } = useGraphqlClients()
  const { region } = useLocalization()
  const { cart, addGlobalAttributes, status: cartStatus } = useCart()

  // Initial request to determine if the user is part of an organization/legacy portal
  const {
    loading: orgIdentityLoading,
    error: orgIdentityError,
    data: orgIdentityData,
  } = useQuery(ORG_IDENTITY_QUERY, {
    client: personalizedClient,
    notifyOnNetworkStatusChange: true,
    skip: !isLoggedIn,
  })

  // Derive a users candidacy for portal/organization associations
  const withPortalAssociation = Boolean(orgIdentityData?.my.portal?.id)
  const withOrganizationAssociation = Boolean((orgIdentityData?.my.organizations.length ?? 0) > 0)

  // Request the users associated portal and/or organization data based on the response of the identity query.
  const {
    loading: orgAssociationLoading,
    error: orgAssociationError,
    data: orgAssociationData,
  } = useOrganizationAssociations({
    withPortalAssociation,
    withOrganizationAssociation,
    skip: Boolean(
      !isLoggedIn ||
        orgIdentityLoading ||
        orgIdentityError ||
        (!withPortalAssociation && !withOrganizationAssociation)
    ),
  })

  const portal = useMemo<UseOrganizationConfigResult['portal']>(() => {
    if (!isLoggedIn) return undefined
    return orgAssociationData?.my.portal ?? undefined
  }, [orgAssociationData?.my.portal, isLoggedIn])

  /** Derive the user's associated organizations */
  const organizations = useMemo<UseOrganizationConfigResult['organizations']>(() => {
    if (!isLoggedIn) return undefined
    return orgIdentityData?.my.organizations ?? undefined
  }, [isLoggedIn, orgIdentityData?.my.organizations])

  /** Derive the user's "active" organization */
  const organization = useMemo<OrganizationMinimalFieldsFragment | undefined>(() => {
    if (!isLoggedIn) return undefined
    return orgAssociationData?.my.activeTeamStore?.organization
  }, [isLoggedIn, orgAssociationData?.my.activeTeamStore])

  /** Derive the user's "active" team store */
  const teamStore = useMemo<
    | (TeamStoreMinimalFieldsFragment &
        TeamStoreEmbroideryConstraintFieldsFragment &
        TeamStoreCollectionIdentityFieldsFragment &
        TeamStoreDiscountPercentageFieldsFragment)
    | undefined
  >(() => {
    if (!isLoggedIn) return undefined
    // Associate the first enabled team store that has a status of `LIVE` as the users active store
    return orgAssociationData?.my.activeTeamStore ?? undefined
  }, [isLoggedIn, orgAssociationData?.my.activeTeamStore])

  /** The placements the active team store offers per category (the same source the admin dashboard uses). */
  const teamStoreAllowedEmbroideryPlacements = useMemo<
    UseOrganizationConfigResult['teamStoreAllowedEmbroideryPlacements']
  >(() => {
    if (!isLoggedIn) return undefined
    return orgAssociationData?.teamStoreAllowedEmbroideryPlacements ?? undefined
  }, [isLoggedIn, orgAssociationData?.teamStoreAllowedEmbroideryPlacements])

  // Sync organization cart attributes when org data is fetched and cart is ready
  useEffect(() => {
    if (
      cartStatus !== 'resolved' ||
      !organization ||
      !teamStore ||
      !teamStore.organizationCollection
    ) {
      return
    }

    const existingOrgId = cart.attributes.find(a => a.key === 'organizationId')?.value
    const existingTeamStoreId = cart.attributes.find(a => a.key === 'teamStoreId')?.value
    const existingOrgCollectionId = cart.attributes.find(a => a.key === 'organizationCollectionId')
      ?.value

    // Only update if attributes differ from what's already on the cart
    if (
      existingOrgId !== organization.id ||
      existingTeamStoreId !== teamStore.id ||
      existingOrgCollectionId !== teamStore.organizationCollection.id
    ) {
      addGlobalAttributes([
        { key: 'organizationId', value: organization.id },
        ...(organization.name ? [{ key: 'organizationName', value: organization.name }] : []),
        ...(teamStore.id ? [{ key: 'teamStoreId', value: teamStore.id }] : []),
        { key: 'organizationCollectionId', value: teamStore.organizationCollection.id },
      ])
    }
  }, [cart.attributes, cartStatus, addGlobalAttributes, organization, teamStore])

  const stipend = useMemo<UseOrganizationConfigResult['stipend']>(() => {
    if (!isLoggedIn) return null
    return orgAssociationData?.my.profile.stipend ?? null
  }, [orgAssociationData?.my.profile.stipend, isLoggedIn])

  const stipendCad = useMemo<UseOrganizationConfigResult['stipendCad']>(() => {
    if (!isLoggedIn) return null
    if (region.id !== 'CA') return null
    return orgAssociationData?.my.profile.stipendCad ?? null
  }, [orgAssociationData?.my.profile.stipendCad, isLoggedIn, region.id])

  // Derive the discount percentage to display on-site for the user's active experience
  const discountPercentage = useMemo(() => {
    if (teamStore) return teamStore.discountPercentage
    return portal?.discountPercentage ?? 0
  }, [teamStore, portal?.discountPercentage])

  const status = useMemo<UseOrganizationConfigResult['status']>(() => {
    if (!isLoggedIn) return 'resolved'
    if (orgIdentityError || orgAssociationError) return 'rejected'
    if (orgIdentityLoading || orgAssociationLoading) return 'pending'

    return 'resolved'
  }, [orgAssociationLoading, orgAssociationError, orgIdentityLoading, orgIdentityError, isLoggedIn])

  const redirectWhenNotAvailable = useCallback<
    UseOrganizationConfigResult['redirectWhenNotAvailable']
  >(
    options => {
      if (!isLoggedIn) {
        navigateToLogin(options)
      } else {
        window.location.replace('/')
      }
    },
    [isLoggedIn, navigateToLogin]
  )

  // Check if the current portal enforces mandatory text embroidery for a given product category
  const hasMandatoryTextEmbroidery = useCallback<
    UseOrganizationConfigResult['hasMandatoryTextEmbroidery']
  >(
    ({ categoryId, categoryKey }) => {
      // For users with an organization association
      if (teamStore) {
        if (!categoryKey) return false
        const categorySettings = teamStore.categoryTextEmbroiderySettings.find(
          cte => cte.categoryKey === categoryKey
        )
        return Boolean(categorySettings?.required && (categorySettings.enabled ?? true))
      }

      // For users with a legacy portal association
      return Boolean(
        categoryId && portal?.embroidery?.mandatoryTextEmbroideryCategories.includes(categoryId)
      )
    },
    [portal?.embroidery?.mandatoryTextEmbroideryCategories, teamStore]
  )

  // Check if the current portal enforces mandatory logo embroidery for a given product category
  const hasMandatoryLogoEmbroidery = useCallback<
    UseOrganizationConfigResult['hasMandatoryLogoEmbroidery']
  >(
    ({ categoryId, categoryKey }) => {
      // For users with an organization association
      if (teamStore) {
        if (!categoryKey) return false
        const categorySettings = teamStore.categoryLogoEmbroiderySettings.find(
          cle => cle.categoryKey === categoryKey
        )
        return Boolean(categorySettings?.required && (categorySettings.enabled ?? true))
      }

      // For users with a legacy portal association
      return Boolean(
        categoryId && portal?.embroidery?.mandatoryLogoEmbroideryCategories.includes(categoryId)
      )
    },
    [portal?.embroidery?.mandatoryLogoEmbroideryCategories, teamStore]
  )

  const hasMandatoryEmbroidery = useCallback<UseOrganizationConfigResult['hasMandatoryEmbroidery']>(
    inputProps => hasMandatoryTextEmbroidery(inputProps) || hasMandatoryLogoEmbroidery(inputProps),
    [hasMandatoryLogoEmbroidery, hasMandatoryTextEmbroidery]
  )

  const b2bStorefrontCollection = useMemo(() => {
    return teamStore?.organizationCollection
      ? {
          name: teamStore.name,
          handle: teamStore.organizationCollection.catalogCollection.handle,
        }
      : portal
      ? {
          name: portal.name,
          handle: portal.collectionHandle,
        }
      : null
  }, [portal, teamStore?.name, teamStore?.organizationCollection])

  return {
    organization,
    organizations,
    teamStore,
    teamStoreAllowedEmbroideryPlacements,
    portal,
    b2bStorefrontCollection,
    stipend,
    stipendCad,
    discountPercentage,
    status,
    error: orgAssociationError,
    redirectWhenNotAvailable,
    hasMandatoryEmbroidery,
    hasMandatoryTextEmbroidery,
    hasMandatoryLogoEmbroidery,
  }
}

const OrganizationConfigContainer = createContainer(useOrganizationConfigImpl)
OrganizationConfigContainer.Provider.displayName = 'OrganizationProviderImpl'
export const OrganizationProvider = OrganizationConfigContainer.Provider
export const useOrganization = OrganizationConfigContainer.useContainer
